import React, { useEffect, useRef, useState } from "react";
import { Modal, LoadingOverlay } from "@mantine/core"; // Added LoadingOverlay here
import Swal from "sweetalert2";

import moment from "moment";
import axios from "axios";
import base_url from "../../../../../../api/bootapi";
import {
  isLufthansaClaim,
  checkIsLufthansaByClaimType,
  isBaggageOrLostWagesClaim,
  downloadAgreementPdf,
} from "../../../../../../components/utils/airlineUtils.js";




const UnifiedAgreementModal = ({
  isOpen,
  onRequestClose,
  claimData,
  setValue, 
  setIsDocusignModalOpen,
}) => {
  const contentRef = useRef();
  const fileInputRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false); // Renamed for clarity
  const isMobile = window.innerWidth < 768;

  // 1. Extract and map data from your provided structure
  const personalDetails = claimData?.personal_details || {};
  console.log("🚀 ~ UnifiedAgreementModal ~ personalDetails:", personalDetails)
  const flightDetails = claimData?.original_flight_details || [];
  const actualFlightDetails = claimData?.actual_flight_details || [];
  const familyMembers = claimData?.travelers || [];
  const typeOfClaim = claimData?.type_of_claim || "";

  const isLufthansa = Boolean(
    claimData?.isLufthansa !== undefined
      ? claimData.isLufthansa
      : checkIsLufthansaByClaimType({
          typeOfClaim,
          originalFlights: flightDetails,
          actualFlights: actualFlightDetails,
        })
  );

  // const clientName = `${personalDetails.title ? personalDetails.title + " " : ""}${personalDetails.first_name || ""} ${personalDetails.last_name || ""}`.trim();
  const clientName = `${personalDetails.name_prefix ? personalDetails.name_prefix + " " : ""}${personalDetails.first_name || ""} ${personalDetails.last_name || ""}`.trim();
  const clientAddress = [
    personalDetails.address?.label || personalDetails.address,
    personalDetails.unit,
    personalDetails.city,
    personalDetails.state,
    personalDetails.postal_code
  ].filter(Boolean).join(", ");
  
  const clientEmail = personalDetails.email_id;
  const passportNumber = personalDetails.passport_number;
  const clientTicketNumber = personalDetails.tickets;
  const currentDate = new Date();

  // 2. Helper Functions
  const formatDOB = (dateOfBirth) => {
    if (!dateOfBirth) return "";
    const formats = [
      "DD-MM-YYYY", "MM-DD-YYYY", "YYYY-MM-DD",
      "DD/MM/YYYY", "MM/DD/YYYY", "YYYY/MM/DD",
      "DD.MM.YYYY", "MM.DD.YYYY",
    ];
    const parsed = moment(dateOfBirth, formats, true);
    if (!parsed.isValid()) {
      return dateOfBirth; 
    }
    return parsed.format("MM-DD-YYYY");
  };

  const getClaimType = (type) => {
    const types = {
      overbooking: "Denied boarding",
      denied_boarding: "Denied boarding",
      lost_baggage: "Lost luggage",
      damage_baggage: "Damaged luggage",
      travel_expenses: "Travel expenses",
      delayed_baggage: "Delayed luggage",
      delayed: "Delayed",
      cancelled: "Cancelled",
      "lost/damaged": "Lost/Damaged",
      lost_wages: "Lost Wages",
    };
    return types[type?.toLowerCase()] || type || "Others";
  };


 

  const current_date = moment().format("MM/DD/YYYY"); // Uses current date for "Concluded on..."
    
  // 3. Handle DocuSign API Call
  const handleSignature = async () => {

     const button = contentRef.current.querySelector("#sign-button");

  if (button) {
    button.style.display = "none";
  }
    const generatedHtml = contentRef.current ? contentRef.current.innerHTML : "";

    onRequestClose();
    setIsSubmitting(true); // Replaces setbackdropopen

    const formObject = {
      signer_email: clientEmail,
      signer_name: clientName,
      return_url: "https://linsoatours.com/#/thank-you",
      html_string: generatedHtml,
    };

    const formData = new FormData();
    formData.append("data", JSON.stringify(formObject));

    const sign_url = `${base_url.api1}/tourapp/wesign_doc`;

    try {
      const response = await axios.post(sign_url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data?.envelope) {
        setValue("signUrl", response.data.envelope.envelope_url);
        setValue("envelopeId", response.data.envelope.envelope_id);
        setValue("agreementUrl", response.data.envelope.envelope_url);
        
        setIsDocusignModalOpen(true);
        window.history.pushState({ modal: true }, "");
      }
    } catch (error) {
      console.error("Error creating envelope:", error);
    } finally {
      setIsSubmitting(false); // Stop loader
    }
  };

  // 4. Sub-components
  const relevantFlights = isBaggageOrLostWagesClaim(typeOfClaim)
    ? (actualFlightDetails.length > 0 ? actualFlightDetails : flightDetails)
    : flightDetails;

  const renderClaimText = (flight) => {
    const airlineName =
      (typeof flight?.airline === "string" ? flight.airline : flight?.airline?.label || flight?.airline?.airline_name) ||
      (typeof flight?.actual_airline === "string" ? flight.actual_airline : flight?.actual_airline?.label || flight?.actual_airline?.airline_name) ||
      (isLufthansa ? "Lufthansa" : "the airline");
    const flightNum = flight?.flight_number || flight?.actual_flight_number || "";
    const flightDate = flight?.date_of_travel || flight?.actual_date_of_travel || "";

    return (
      <>
        The Client, acting on its own behalf [and on behalf of the persons it is
        representing], declares that it has claims against {airlineName}{" "}
        for compensation for:
        <ol>
          <li>
            {getClaimType(typeOfClaim)} flight ({flightNum} and {formatDOB(flightDate)})
          </li>
        </ol>
        increased by potential claims for damages suffered and interest for late payment (the <strong>“Claims”</strong>).
      </>
    );
  };

  const FlightDetailsList = () => {
    if (relevantFlights.length > 1) {
      return (
        <ol style={{ paddingLeft: "20px" }}>
          <li>
            <ul style={{ listStyleType: "lower-roman", paddingLeft: "20px" }}>
              {relevantFlights.map((flight, index) => (
                <React.Fragment key={index}>
                  <li>{renderClaimText(flight)}</li>
                  {index < relevantFlights.length - 1 && <br />}
                </React.Fragment>
              ))}
            </ul>
          </li>
        </ol>
      );
    } else if (relevantFlights.length === 1 && (relevantFlights[0].flight_number || relevantFlights[0].actual_flight_number)) {
      return <p>1. {renderClaimText(relevantFlights[0])}</p>;
    } else if (relevantFlights.length === 1) {
      return <p>1. {renderClaimText(relevantFlights[0])}</p>;
    }
    return <p>No flight details available.</p>;
  };

  return (
    <>
      {/* Mantine Loading Overlay replaces MUI Backdrop & CircularProgress */}
      <LoadingOverlay 
        visible={isSubmitting} 
        zIndex={3000} 
        fixed // Ensures it covers the entire screen, not just the parent container
        overlayProps={{ radius: "sm", blur: 1 }} // Adds a nice blur effect behind the loader
      />

      <Modal
        opened={isOpen}
        onClose={onRequestClose}
        title="Agreement Details"
        size="800px"
        padding="md"
        fullScreen={isMobile}
      >
        <div style={{ padding: "8px 0" }}>
          <div
            ref={contentRef}
            style={{
              position: "relative",
              padding: "16px",
              backgroundColor: "#fff",
              borderRadius: "8px",
              boxShadow: "1px 1px 5px rgba(0, 0, 0, 0.1)",
              breakInside: "avoid",
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            }}
          >

        <h1 style={{ textAlign: "center", marginBottom: "30px", fontSize: '18px', textTransform: 'uppercase', fontWeight: '600' }}>
          Assignment Agreement
        </h1>

        <p style={{ marginBottom: "16px" }}>
          Concluded on {current_date} by and between:
        </p>

        <p >
          {clientName}, residing at {clientAddress}, passport number{" "}
          {passportNumber}, booking number {clientTicketNumber},
        </p>
        <p>
          hereinafter referred to as the <strong>“Client”</strong>,
        </p>

        {familyMembers.length > 0 && (
          <>
            <p>[acting at the same time on behalf of:</p>
            <ol>
              {familyMembers.map((member, index) => (
                <li key={index}>
                  <p>
                    {member.name_prefix ? `${member.name_prefix} ` : ""}{member.first_name} {member.last_name}, born{" "}
                    {formatDOB(member.date_of_birth)}, passport number{" "}
                    {member.passport_number}, ticket number{" "}
                    {member.tickets}
                  </p>
                </li>
              ))}
            </ol>
          </>
        )}

        <p>and</p>

        <p>
          Fly And Claim Inc, 11 Main Street, Monsey NY 10952. USA, EIN: 41-3703352
          (hereinafter <strong>“F&C USA”</strong>),
        </p>

        <p>
          and
        </p>

        <p>
          Fly and Claim Travel Services Sp. z o.o., ul. Mogilska 43, 31-545 Kraków, Poland, KRS:
          0001247476, NIP: 6751829771, REGON: 54500165800000 ( hereinafter <strong>“F&C Poland”</strong>),
        </p>
        <p>
          hereinafter together referred to as the <strong>“Provider”</strong>.
        </p>

        <hr style={{ margin: "20px 0" }} />

        <section style={{ marginBottom: "24px" }}>
          <h2 style={{ textAlign: "center", marginBottom: "16px" }}>
            <strong>§1</strong>
          </h2>
          <p>
            <FlightDetailsList />
          </p>
          <p>
            2. The Client declares that he remains fully entitled to the Claims and that it has not
            been assigned to any third parties nor have any third parties been engaged in trying
            to assert the Claims.
          </p>
          <p>
            3. The Client hereby assigns the Claims and all rights arising therein to F&C USA and
            F&C Poland, allowing them to assert the Claims in their own name (either jointly or
            independently) and to conduct all legal action, negotiations and make any
            settlements with the airline at its discretion. F&C USA and F&C Poland are further
            entitled to collect any and all compensation received from the airline (either jointly or independently - at their discretion) and are later obliged to transfer it to the Client
            after offsetting the agreed fee.
          </p>
          <p>
            4. F&C USA and F&C Poland act with due diligence and strive to acquire compensation
            on behalf of the Client. F&C USA and F&C Poland do not, however, guarantee that
            their services will be successful in acquiring the compensation and may therefore not
            be held liable solely based on the fact that the services did not result in receipt of
            compensation. The Client is aware that it may take many months (even years)
            before any compensation is potentially acquired.
          </p>

          <p>
            5. Additional clauses related to this Assignment Agreement, in particular the fee
            charged by F&C USA and F&C Poland, obligations of the Parties and limitations of
            liability, are subject to the Fly and Claim’s Terms and Conditions which constitute an
            integral part of this Assignment Agreement.
          </p>

          <p>
            6. F&C USA and F&C Poland may pursue the claims on any legal basis and in any
            jurisdiction/country that may be applicable.
          </p>
        </section>

        <hr style={{ margin: "20px 0" }} />

        <section style={{ marginBottom: "24px" }}>
          <h2 style={{ textAlign: "center", marginBottom: "16px" }}>
            <strong>§2</strong>
          </h2>
          <p>
            1. This Assignment Agreement is subject to {isLufthansa ? "German" : "Polish"} law and the jurisdiction of {isLufthansa ? "German" : "Polish"} courts.
          </p>
          <p>2. The Assignment Agreement was concluded by day of digital signatures.</p>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
            <div style={{ textAlign: "center" }}>
              <p style={{ marginBottom: "10px" }}>
                <div
                  style={{ width: "280px", height: "70px" }}
                  className="signatureContainer"
                ></div>
                __________________________________
              </p>
              <p>
                <strong>F&C Poland</strong>
              </p>
            </div>
            <div style={{ textAlign: "center" }}>
              <p style={{ marginBottom: "10px" }}>
                <div style={{ width: "280px", minHeight: "70px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {isLufthansa ? (
                    <div style={{ width: "280px", height: "70px" }}></div>
                  ) : (
                    <button
                      id="sign-button"
                      style={{
                        maxHeight: "40px",
                        top: "39px",
                        left: "10px",
                        position: "relative",
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        padding: "8px 16px",
                        cursor: "pointer",
                      }}
                      onClick={handleSignature}
                    >
                      Sign Document
                    </button>
                  )}
                </div>
                __________________________________
              </p>
              <p>
                <strong>Client signature</strong>
              </p>
            </div>

            <div style={{ textAlign: "center" }}>
              <p style={{ marginBottom: "10px" }}>
                <div
                  style={{ width: "280px", height: "70px" }}
                  className="signatureContainer"
                ></div>
                __________________________________
              </p>
              <p>
                <strong>F&C USA</strong>
              </p>
            </div>
          </div>
        </section>
        </div>

        {isLufthansa && (
          <div
            style={{
              marginTop: "20px",
              padding: "16px",
              backgroundColor: "#f0f7ff",
              border: "1px solid #bbdefb",
              borderRadius: "8px",
              textAlign: "left",
            }}
          >
            <p
              style={{
                margin: "0 0 6px 0",
                fontSize: "14px",
                fontWeight: "600",
                color: "#0d47a1",
              }}
            >
              Lufthansa Physical Wet-Ink Signature Required for Assignment Agreement
            </p>
            <p
              style={{
                margin: "0 0 12px 0",
                fontSize: "13px",
                color: "#37474f",
                lineHeight: "1.5",
              }}
            >
              1. Click <strong>Download Agreement (PDF)</strong> or <strong>Print</strong> to obtain your agreement document.<br />
              2. Print the document and physically sign on the <strong>Client signature</strong> line.<br />
              3. Click <strong>Upload Signed Agreement</strong> below to upload the signed file.
            </p>

            <div
              style={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <button
                type="button"
                style={{
                  backgroundColor: "#0d47a1",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  padding: "8px 14px",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "13px",
                }}
                onClick={async () => {
                  setIsSubmitting(true);
                  try {
                    await downloadAgreementPdf(
                      contentRef.current,
                      "Assignment_Agreement.pdf"
                    );
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
              >
                Download Agreement (PDF)
              </button>

              <button
                type="button"
                style={{
                  backgroundColor: "#2e7d32",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  padding: "8px 14px",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "13px",
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload Signed Agreement
              </button>

              {/* <button
                type="button"
                style={{
                  backgroundColor: "#f5f5f5",
                  color: "#333",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "8px 14px",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "13px",
                }}
                onClick={() => window.print()}
              >
                Print
              </button> */}

              <input
                type="file"
                ref={fileInputRef}
                accept=".pdf,.png,.jpg,.jpeg"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setValue("agreement", file);
                    setValue("agreement_signed", true);
                    setValue("is_agreement_checked", true);
                    onRequestClose();
                    Swal.fire({
                      icon: "success",
                      title: "Signed Agreement Uploaded",
                      text: `${file.name} uploaded successfully!`,
                      confirmButtonColor: "#0d47a1",
                    });
                  }
                }}
              />
            </div>
          </div>
        )}
       
      </div>
      </Modal>
    </>
  );
};

export default UnifiedAgreementModal;